import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { SupabaseAuth } from '../utils/supabaseAuth'; // Import SupabaseAuth

export interface AuthUser {
  id: string | number;      // auth_id (UUID do Supabase Auth) - usar para RLS
  dbId?: string | number;   // id da tabela usuarios - usar para FKs
  name: string;
  nome?: string;
  email: string;
  role: string;
  setor: string;
  cargo?: string;
  departamento?: string;
  telefone?: string;
  ramal?: string;
  picture?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  canPublishMural: () => boolean;
  canModerate: () => boolean;
  isAdmin: () => boolean;
  reload: () => Promise<void>; // Added reload function
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    setLoading(true);
    try {
      console.log('[AUTH-CONTEXT] Checking for existing session...');
      const currentUser = await SupabaseAuth.getCurrentUser();
      if (currentUser) {
        setUser({
          id: currentUser.id,           // auth_id para RLS
          dbId: currentUser.db_id,      // id da tabela usuarios para FKs
          name: currentUser.nome,
          nome: currentUser.nome,
          email: currentUser.email,
          role: currentUser.role,
          setor: currentUser.setor,
          cargo: currentUser.cargo,
          departamento: currentUser.departamento,
          telefone: currentUser.telefone,
          ramal: currentUser.ramal,
          picture: currentUser.picture,
        });
        console.log('[AUTH-CONTEXT] Session restored for user:', currentUser.nome, '(auth_id:', currentUser.id, 'db_id:', currentUser.db_id, ')');
      } else {
        console.log('[AUTH-CONTEXT] No active session found');
        setUser(null);
      }
    } catch (error) {
      console.error('[AUTH-CONTEXT] Session check failed:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        console.error('[AUTH-CONTEXT] ⚠️ Cannot connect to Supabase. Please check if the Supabase project is active.');
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<AuthUser> => {
    setLoading(true);
    try {
      console.log('[AUTH-CONTEXT] Signing in with email:', email);
      
      // Try both direct email and username formats
      let userProfile;
      try {
        const result = await SupabaseAuth.signIn(email, password);
        userProfile = result.userProfile;
      } catch (error) {
        // If email format failed, try username format
        if (error instanceof Error && error.message.includes('Invalid login credentials') && !email.includes('@')) {
          console.log('[AUTH-CONTEXT] Email format failed, trying username format');
          const emailFormat = `${email}@grupocropfield.com.br`;
          const result = await SupabaseAuth.signIn(emailFormat, password);
          userProfile = result.userProfile;
        } else {
          throw error;
        }
      }

      const authenticatedUser: AuthUser = {
        id: userProfile.id,           // auth_id para RLS
        dbId: userProfile.db_id,      // id da tabela usuarios para FKs
        name: userProfile.nome,
        nome: userProfile.nome,
        email: userProfile.email,
        role: userProfile.role,
        setor: userProfile.setor,
        cargo: userProfile.cargo,
        departamento: userProfile.departamento,
        telefone: userProfile.telefone,
        ramal: userProfile.ramal,
        picture: userProfile.picture,
      };
      setUser(authenticatedUser);
      console.log('[AUTH-CONTEXT] ✅ Login successful for:', authenticatedUser.name, '(auth_id:', authenticatedUser.id, 'db_id:', authenticatedUser.dbId, ')');
      return authenticatedUser;
    } catch (error) {
      console.error('[AUTH-CONTEXT] Sign in error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    console.log('[AUTH-CONTEXT] Signing out user:', user?.name);
    try {
      await SupabaseAuth.signOut();
      console.log('[AUTH-CONTEXT] Logout request successful');
    } catch (error) {
      console.error('[AUTH-CONTEXT] Logout request failed:', error);
    }
    setUser(null);
    console.log('[AUTH-CONTEXT] User state cleared, logout complete');
  };

  const canPublishMural = () => {
    if (!user) return false;
    return SupabaseAuth.canPublishMural(user);
  };

  const canModerate = () => {
    if (!user) return false;
    return SupabaseAuth.canModerate(user);
  };

  const isAdmin = () => {
    if (!user) return false;
    return SupabaseAuth.isAdmin(user);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        signIn,
        signOut,
        canPublishMural,
        canModerate,
        isAdmin,
        reload: fetchCurrentUser, // Provide reload function
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;